import React, { useState } from 'react';
import { Colors, Dropdown, Menu } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import styled from 'styled-components/macro';
import { Checkbox, Spin, notification } from 'antd';
import { gql, useApolloClient } from '@apollo/client';
import {
  GQLOperations,
  Rule,
  SchemaCollection,
  useGetDigitalTwinQuery,
  useToggleDisableRuleMutation,
} from 'types/dt-graphql-apollo-v2';
import { deleteRuleQuery } from 'utils/data-validation-graphql';
import { term } from 'utils/project-ruleset';
import { ConfirmationDialog } from 'components';
import { useUrl } from 'hooks/useUrls';
import { useAccessControl } from 'hooks/useAccessControl';
import { JobType } from 'hooks/useStartValidationJob';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { RunRulesButton } from './RunRulesButton';
import { isRuleDisabled } from '../helpers';

export const RuleOptions = ({
  rule,
  onDeleteRuleComplete,
  onEditRule,
}: {
  rule: Rule;
  onDeleteRuleComplete: VoidFunction;
  onEditRule: (rule: Rule) => void;
}) => {
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const apolloClient = useApolloClient();
  const digitalTwinId = useUrl.DigitalTwinId();
  const schemaNameDT = useUrl.SchemaNameDT();

  const isDisabled = isRuleDisabled(rule);

  const hasAccess = useAccessControl();
  const canEditRule = hasAccess(['Update_Rule']);
  const canDeleteRule = hasAccess(['Delete_Rule']);
  const canRunRule = hasAccess(['Add_Rule_ruleRunStatus']) && !isDisabled;

  const { data: digitalTwinData } = useGetDigitalTwinQuery({
    variables: {
      id: digitalTwinId,
    },
  });
  const [toggleDisableRule, { loading: updatingToggle }] =
    useToggleDisableRuleMutation();

  const currentSchema =
    digitalTwinData?.listDigitalTwin?.items[0]?.schemaCollections?.items.find(
      (schema) => schema?.name === schemaNameDT
    ) as SchemaCollection;

  const onDeleteRule = async () => {
    try {
      const query = deleteRuleQuery(rule);
      await apolloClient.mutate({
        mutation: gql(query),
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      onDeleteRuleComplete();
    } catch (err: any) {
      notification.error({
        message: `Could not delete ${term.rule}`,
        description: err.toString(),
      });
    }
  };

  const onHandleEditRule = (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation();
    onEditRule(rule);
  };

  const onHandleDeleteRule = (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setConfirmDeleteDialog(true);
  };

  const onCompleteDelete = async () => {
    setLoadingDelete(true);
    await onDeleteRule();
    setConfirmDeleteDialog(false);
    setLoadingDelete(false);
  };

  const onToggleEnableRule = async (e: CheckboxChangeEvent) => {
    const tags = !e.target.checked
      ? (rule.tags || []).filter((tag) => tag !== '_disabled')
      : [...(rule.tags || []), '_disabled'];
    try {
      await toggleDisableRule({
        variables: {
          id: rule.externalId,
          tags,
        },
        refetchQueries: [
          GQLOperations.Query.GetMilestones,
          GQLOperations.Query.GetRulesets,
          GQLOperations.Query.GetRules,
        ],
      });
    } catch (error) {
      notification.error({
        message: "Couldn't enable/disable rule.",
      });
    }
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      aria-hidden="true"
      role="link"
    >
      {(canEditRule || canDeleteRule || canRunRule) && (
        <Dropdown
          content={
            <StyledOptionsMenu>
              {canEditRule && (
                <Menu.Item
                  onClick={onHandleEditRule}
                  icon="Edit"
                  iconPlacement="left"
                >
                  <span>Edit {term.rule}</span>
                </Menu.Item>
              )}
              {canRunRule && (
                <RunRulesButton
                  buttonIcon="Play"
                  buttonText="Run rule"
                  buttonType="ghost"
                  entityId={rule.externalId}
                  schemaCollectionRefId={currentSchema?.refId}
                  type={JobType.RULE}
                />
              )}
              <Menu.Item>
                <Checkbox
                  disabled={updatingToggle}
                  onChange={onToggleEnableRule}
                  defaultChecked={isDisabled}
                >
                  {isDisabled ? 'Rule disabled' : 'Disable rule'}

                  {updatingToggle && <Spin size="small" />}
                </Checkbox>
              </Menu.Item>
              {canDeleteRule && (
                <Menu.Item
                  onClick={onHandleDeleteRule}
                  icon="Delete"
                  iconPlacement="left"
                  destructive
                >
                  <span>Delete {term.rule} from project</span>
                </Menu.Item>
              )}
            </StyledOptionsMenu>
          }
        >
          <StyledOptionsIcon
            style={{ verticalAlign: 'middle' }}
            type="EllipsisVertical"
            size={18}
          />
        </Dropdown>
      )}

      {confirmDeleteDialog && (
        <ConfirmationDialog
          title={`Delete ${term.rule}`}
          message={`Are you sure you want to delete the ${term.rule} "${rule.name}"?`}
          textOk={`Yes, delete ${term.rule}`}
          buttonTypeOk="destructive"
          onOk={() => onCompleteDelete()}
          width={400}
          onClose={() => setConfirmDeleteDialog(false)}
          loading={loadingDelete}
        />
      )}
    </div>
  );
};

const StyledOptionsMenu = styled(Menu)`
  min-width: 100px !important;

  .cogs-menu-item {
    padding-left: 0.8rem;
  }

  .delete-rule {
    color: var(--cogs-danger);
  }
`;

const StyledOptionsIcon = styled(Icon)`
  &:hover {
    cursor: pointer;
    background: var(--cogs-bg-accent);
  }
`;
